import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { apiGet } from '../../lib/api-client';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';

interface StatItem {
  deposit: number;
  topup: number;
  withdraw: number;
  settle: number;
  myPayinShare: number;
  myPayoutShare: number;
  payinSharePercent: number;
  payoutSharePercent: number;
}

export function SubSellerDashboard() {
  const { t } = useTranslation();

  const { data } = useQuery({
    queryKey: ['sub-seller', 'stats'],
    queryFn: () =>
      apiGet<{ items: StatItem[] }>('/sub-seller/stats', { pageSize: '100' }),
  });

  const stats = data?.ok ? data.data.items : [];

  const totalDeposit = stats.reduce((sum, s) => sum + s.deposit, 0);
  const totalTopup = stats.reduce((sum, s) => sum + s.topup, 0);
  const totalWithdraw = stats.reduce((sum, s) => sum + s.withdraw, 0);
  const totalSettle = stats.reduce((sum, s) => sum + s.settle, 0);
  const totalPayinShare = stats.reduce((sum, s) => sum + s.myPayinShare, 0);
  const totalPayoutShare = stats.reduce((sum, s) => sum + s.myPayoutShare, 0);
  const payinPct = stats.length > 0 ? stats[0].payinSharePercent : 0;
  const payoutPct = stats.length > 0 ? stats[0].payoutSharePercent : 0;

  const formatNum = (n: number) =>
    n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{t('dashboard.title')}</h1>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader>
            <CardTitle>{t('dashboard.totalDeposits')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{formatNum(totalDeposit)}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('dashboard.totalTopups')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">{formatNum(totalTopup)}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('dashboard.totalWithdraws')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">{formatNum(totalWithdraw)}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('dashboard.totalSettles')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-600">{formatNum(totalSettle)}</div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>{t('transactions.calcPayinFee')} ({payinPct}%)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{formatNum(totalPayinShare)}</div>
            <div className="mt-1 text-xs text-gray-500">({t('transactions.deposit')} + {t('transactions.topup')}) × {payinPct}%</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('transactions.calcPayoutFee')} ({payoutPct}%)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600 dark:text-green-400">{formatNum(totalPayoutShare)}</div>
            <div className="mt-1 text-xs text-gray-500">({t('transactions.withdraw')} + {t('transactions.settle')}) × {payoutPct}%</div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
